import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { kioskApi } from "@/lib/api";
import { Clock, MapPin, ShieldAlert, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

const SEV_COLOR = {
  critical: "#FF2E63", high: "#FF6B35", medium: "#FFC542", low: "#00F0FF",
};

/**
 * KioskBriefing — unauthenticated, kiosk-token-gated shift briefing viewer.
 * URL: /kiosk/briefing?token=<raw>
 *
 * Requires a kiosk token with `shift_briefing` scope. Read-only.
 */
export default function KioskBriefing() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [briefing, setBriefing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setError("Missing kiosk token in URL."); return; }
    let ignore = false;
    async function load() {
      try {
        const r = await kioskApi.get(`/kiosk/shift-briefing/latest?token=${encodeURIComponent(token)}`);
        if (!ignore) setBriefing(r.data.briefing || null);
      } catch (e) {
        if (!ignore) {
          setError(e.response?.status === 401
            ? "Kiosk token is invalid, revoked, or expired."
            : e.response?.status === 403
              ? "This kiosk token does not have shift_briefing scope."
              : "Failed to load briefing.");
        }
      }
    }
    load();
    // Refresh every 5 minutes so an always-on TV shows the freshest digest.
    const t = setInterval(load, 5 * 60 * 1000);
    // Presence heartbeat — every 20s so the admin sees this wall as "online".
    const ping = () => kioskApi.post(`/kiosk/heartbeat?token=${encodeURIComponent(token)}`).catch(() => {});
    ping();
    const hb = setInterval(ping, 20000);
    return () => { ignore = true; clearInterval(t); clearInterval(hb); };
  }, [token]);

  if (error) {
    return (
      <div className="h-screen w-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="kiosk-briefing-error">
        <div className="max-w-md text-center">
          <ShieldAlert className="w-12 h-12 text-[#FF2E63] mx-auto mb-4" />
          <h1 className="font-display font-black text-2xl mb-2">Access Denied</h1>
          <p className="text-[#8E9196] text-sm font-mono">{error}</p>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="h-screen w-screen bg-[#060606] text-white flex items-center justify-center" data-testid="kiosk-briefing-empty">
        <div className="text-center">
          <Clock className="w-8 h-8 text-[#00F0FF] mx-auto mb-3 animate-pulse" />
          <p className="text-sm font-mono text-[#8E9196]">Loading latest shift briefing…</p>
        </div>
      </div>
    );
  }

  const totals = briefing.totals || {};
  const sev = totals.severity_breakdown || {};
  const delta = totals.delta_pct || 0;

  return (
    <div className="min-h-screen bg-[#060606] text-white p-10" data-testid="kiosk-briefing">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-[#B57BFF]" strokeWidth={1.75} />
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              ForensiQ · Public Shift Digest
            </div>
          </div>
          <h1 className="font-display font-black text-5xl mt-2 tracking-tight" data-testid="kiosk-briefing-shift">
            {briefing.shift_label}
          </h1>
          <div className="text-xs font-mono text-[#52525B] mt-1">
            Generated {new Date(briefing.generated_at).toLocaleString()}
          </div>
        </div>

        {/* Highlights */}
        <div className="border border-white/10 rounded-sm p-8 bg-[#0a0a0a] mb-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-[#FFC542]" /> Highlights
          </div>
          <ul className="space-y-3">
            {(briefing.highlights || []).map((h, i) => (
              <li key={i} className="text-lg text-white/90 flex gap-3 leading-relaxed">
                <span className="text-[#B57BFF]">•</span>{h}
              </li>
            ))}
          </ul>
        </div>

        {/* Big counter grid */}
        <div className="grid grid-cols-6 gap-3 mb-8">
          {["critical", "high", "medium", "low"].map((k) => (
            <div key={k} className="border border-white/10 rounded-sm p-5 text-center bg-[#0a0a0a]">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: SEV_COLOR[k] }}>{k}</div>
              <div className="font-display font-black text-5xl mt-2" style={{ color: SEV_COLOR[k], textShadow: `0 0 20px ${SEV_COLOR[k]}66` }}>
                {sev[k] || 0}
              </div>
            </div>
          ))}
          <div className="border border-white/10 rounded-sm p-5 text-center bg-[#0a0a0a]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">total</div>
            <div className="font-display font-black text-5xl mt-2">{totals.events || 0}</div>
          </div>
          <div className="border border-white/10 rounded-sm p-5 text-center bg-[#0a0a0a]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">vs prev</div>
            <div className="font-display font-black text-5xl mt-2 flex items-center justify-center gap-2"
                 style={{ color: delta > 0 ? "#FF6B35" : delta < 0 ? "#00F0FF" : "#FFF" }}>
              {delta > 0 ? <TrendingUp className="w-8 h-8" /> : delta < 0 ? <TrendingDown className="w-8 h-8" /> : null}
              {delta > 0 ? "+" : ""}{delta}%
            </div>
          </div>
        </div>

        {/* Hotspots */}
        {(briefing.hotspots || []).length > 0 && (
          <div className="border border-white/10 rounded-sm p-6 bg-[#0a0a0a] mb-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-4 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#00F0FF]" /> Top Origin Hotspots
            </div>
            <div className="grid grid-cols-2 gap-x-8">
              {briefing.hotspots.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.04]">
                  <div className="text-base">
                    {h.city}, <span className="text-[#8E9196]">{h.country}</span>
                  </div>
                  <div className="font-mono text-lg" style={{
                    color: h.max_severity_weight >= 4 ? "#FF2E63" : h.max_severity_weight >= 3 ? "#FF6B35" : "#FFC542"
                  }}>{h.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-[9px] font-mono uppercase tracking-widest text-[#52525B] text-center mt-6">
          ForensiQ · Public Digest · Refreshes every 5 min · <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
        </div>
      </div>
    </div>
  );
}
